import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package, User, MapPin, CreditCard, CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, ShieldCheck, Truck, LogIn, UserPlus, Weight, Ruler, Home, Boxes,
  Landmark, PackageCheck,
} from 'lucide-react';
import { AddressAutocomplete } from './AddressAutocomplete';
import PointsMap, { fmtDistance } from './PointsMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La distanza dei punti può mancare (indirizzo non geocodificato): mostriamola solo se numerica.
const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v);

const emptyParty = () => ({
  ragioneSociale: '', referente: '', indirizzo: '', cap: '', citta: '',
  prov: '', nazione: 'IT', telefono: '', email: '',
});

const STEPS = [
  { key: 'account', label: 'Account', icon: User },
  { key: 'mittente', label: 'Mittente', icon: Home },
  { key: 'destinatario', label: 'Destinatario', icon: MapPin },
  { key: 'pacco', label: 'Pacco', icon: Package },
  { key: 'pagamento', label: 'Pagamento', icon: CreditCard },
];

const PartyForm = ({ party, setParty, prefix, onValidate, validation, validating }) => {
  const set = (k, v) => setParty({ ...party, [k]: k === 'email' ? v : (v || '').toUpperCase() });
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Nome e cognome / Referente *</Label>
          <Input data-testid={`${prefix}-referente`} value={party.referente}
            onChange={(e) => set('referente', e.target.value)} placeholder="Mario Rossi" />
        </div>
        <div>
          <Label>Azienda (facoltativo)</Label>
          <Input data-testid={`${prefix}-ragionesociale`} value={party.ragioneSociale}
            onChange={(e) => set('ragioneSociale', e.target.value)} placeholder="ACME Srl" />
        </div>
      </div>
      <div>
        <Label>Indirizzo *</Label>
        <AddressAutocomplete
          testid={`${prefix}-indirizzo`}
          value={party.indirizzo}
          onChange={(v) => set('indirizzo', v)}
          onSelect={(a) => setParty({
            ...party,
            indirizzo: (a.indirizzo || party.indirizzo || '').toUpperCase(),
            cap: (a.cap || party.cap || '').toUpperCase(),
            citta: (a.citta || party.citta || '').toUpperCase(),
            prov: (a.prov || party.prov || '').toUpperCase(),
            nazione: a.nazione || 'IT',
          })}
        />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label>CAP *</Label>
          <Input data-testid={`${prefix}-cap`} value={party.cap}
            onChange={(e) => set('cap', e.target.value)} placeholder="00100" />
        </div>
        <div>
          <Label>Città *</Label>
          <Input data-testid={`${prefix}-citta`} value={party.citta}
            onChange={(e) => set('citta', e.target.value)} placeholder="Roma" />
        </div>
        <div>
          <Label>Provincia *</Label>
          <Input data-testid={`${prefix}-prov`} value={party.prov} maxLength={2}
            onChange={(e) => set('prov', e.target.value.toUpperCase())} placeholder="RM" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Telefono *</Label>
          <Input data-testid={`${prefix}-telefono`} value={party.telefono}
            onChange={(e) => set('telefono', e.target.value)} placeholder="069876543" />
        </div>
        <div>
          <Label>Email *</Label>
          <Input data-testid={`${prefix}-email`} type="email" value={party.email}
            onChange={(e) => set('email', e.target.value)} placeholder="email@example.it" />
        </div>
      </div>

      {onValidate && (
        <div className="pt-2">
          <Button type="button" variant="outline" onClick={onValidate} disabled={validating}
            data-testid={`${prefix}-validate-btn`}>
            {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Verifica indirizzo
          </Button>
          {validation && (
            <div data-testid={`${prefix}-validation-result`}
              className={`mt-3 text-sm rounded-lg p-3 border ${
                validation.status === 'valid' ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              <div className="font-medium flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                {validation.status === 'valid' ? 'Indirizzo valido' : 'Indirizzo da verificare'}
                {typeof validation.confidenceScore === 'number' && ` · affidabilità ${validation.confidenceScore}%`}
              </div>
              {validation.notes?.length > 0 && <div className="mt-1 opacity-90">{validation.notes.join(' · ')}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OccasionalShipping = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [account, setAccount] = useState({ customer_id: null, email: '', first_name: '', mode: 'guest' });
  const [authForm, setAuthForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    tipo: 'privato', company: '', vat: '', codfis: '', sdi: '', pec: '', phone: '',
  });
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);

  const [mittente, setMittente] = useState(emptyParty());
  const [destinatario, setDestinatario] = useState(emptyParty());
  const [collo, setCollo] = useState({ peso: '', larghezza: '', altezza: '', lunghezza: '' });
  const [opts, setOpts] = useState({ contrassegno: '', valore_assicurato: '', note: '' });
  const [ritiro, setRitiro] = useState({ richiesto: false, data: '', note: '' });
  // Consegna a locker InPost (Locker-to-Locker)
  const [locker, setLocker] = useState({ enabled: false, code: '', name: '', address: '' });
  const [lockerSearch, setLockerSearch] = useState({ loading: false, results: null, origin: null });
  // PDB Poste: consegna e/o accettazione (drop-off) presso Punto Poste/Ufficio/Locker Poste
  const [poste, setPoste] = useState({
    consegnaEnabled: false, consegnaTipo: 'PUNTO_POSTE', code: '', name: '', address: '',
    dropoffEnabled: false, dropoffTipo: 'PUNTO_POSTE',
    servizio: 'PDBST',  // PDBST (Standard) | PDBEX (Express)
  });
  const [posteSearch, setPosteSearch] = useState({ loading: false, results: null, origin: null });

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  // Confirmation after Stripe redirect
  const [confirm, setConfirm] = useState(null);
  const sessionId = searchParams.get('session_id');

  const pollStatus = useCallback(async (sid, attempt = 0) => {
    if (attempt > 8) {
      setConfirm({ state: 'timeout' });
      return;
    }
    try {
      const res = await axios.get(`${API}/occasional/checkout/status/${sid}`);
      const d = res.data;
      if (d.payment_status === 'paid') {
        setConfirm({ state: 'paid', order: d.woo_order_number || d.woo_order_id, tracking: d.tracking, label: d.label_url });
      } else if (d.status === 'expired') {
        setConfirm({ state: 'expired' });
      } else {
        setTimeout(() => pollStatus(sid, attempt + 1), 2000);
      }
    } catch (e) {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      setConfirm({ state: 'checking' });
      pollStatus(sessionId);
    }
  }, [sessionId, pollStatus]);

  // Recognize a returning logged-in customer and skip the Account step
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('occ_customer') || 'null');
      if (saved && localStorage.getItem('occ_token')) {
        setAccount({ ...saved, mode: 'account' });
        setMittente((m) => ({ ...m, email: saved.email || m.email }));
        if (!sessionId) setStep(1);
      }
    } catch { /* ignore */ }
  }, [sessionId]);

  const lockerActive = locker.enabled && !!(locker.code || '').trim();
  const posteConsegnaActive = poste.consegnaEnabled && !!(poste.code || '').trim();
  const posteDropoffActive = poste.dropoffEnabled && !!poste.dropoffTipo;
  const posteActive = posteConsegnaActive || posteDropoffActive;
  // Nessun ritiro a domicilio quando il mittente deposita al punto (locker InPost o drop-off Poste)
  const noHomePickup = lockerActive || posteDropoffActive;
  const posteTipoLabel = (t) => ({
    PUNTO_POSTE: 'Punto Poste', UFFICIO_POSTALE: 'Ufficio Postale', LOCKER: 'Locker Poste',
  }[t] || t);

  const buildShipment = () => {
    const contrassegno = parseFloat(opts.contrassegno) || 0;
    const up = (p) => {
      const o = { ...p };
      Object.keys(o).forEach((k) => {
        if (k !== 'email' && typeof o[k] === 'string') o[k] = o[k].toUpperCase();
      });
      return o;
    };
    // Scelta servizio: InPost L2L > PDB Poste (punto) > standard/contrassegno
    let servizio;
    if (lockerActive) servizio = 'INPL2L';
    else if (posteActive) servizio = poste.servizio || 'PDBST';
    else servizio = contrassegno > 0 ? 'S15' : 'S09';
    return {
      mittente: up(mittente),
      destinatario: up(destinatario),
      collo: {
        peso: parseFloat(collo.peso) || 0,
        larghezza: parseFloat(collo.larghezza) || 0,
        altezza: parseFloat(collo.altezza) || 0,
        lunghezza: parseFloat(collo.lunghezza) || 0,
      },
      servizio,
      // Locker-to-Locker InPost: il backend allega accessori.puntoRitiro.
      punto_ritiro: lockerActive ? (locker.code || '').trim().toUpperCase() : '',
      // PDB Poste: consegna e/o accettazione presso punto (backend -> accessori.puntoConsegna/puntoAccettazione).
      punto_consegna: posteConsegnaActive
        ? { tipo: poste.consegnaTipo, codice: (poste.code || '').trim().toUpperCase() }
        : null,
      punto_accettazione: posteDropoffActive ? { tipo: poste.dropoffTipo } : null,
      contrassegno,
      valore_assicurato: parseFloat(opts.valore_assicurato) || 0,
      note: (opts.note || '').toUpperCase(),
      ritiro: {
        richiesto: !noHomePickup && !!ritiro.richiesto,
        data: noHomePickup ? '' : (ritiro.data || ''),
        note: noHomePickup ? '' : (ritiro.note || '').toUpperCase(),
      },
    };
  };

  const searchLockers = async () => {
    if (!destinatario.cap) return toast.error('Inserisci prima il CAP del destinatario');
    setLockerSearch({ loading: true, results: null, origin: null });
    try {
      const res = await axios.get(`${API}/shipping/locker/search`, {
        params: { cap: destinatario.cap, indirizzo: destinatario.indirizzo || '' },
      });
      setLockerSearch({ loading: false, results: res.data.points || [], origin: res.data.origin || null });
    } catch (e) {
      setLockerSearch({ loading: false, results: null, origin: null });
      toast.error(e.response?.data?.detail || 'Ricerca locker non disponibile: inserisci il codice manualmente');
    }
  };

  const searchPoste = async () => {
    if (!destinatario.cap) return toast.error('Inserisci prima il CAP del destinatario');
    setPosteSearch({ loading: true, results: null, origin: null });
    try {
      const res = await axios.get(`${API}/shipping/puntiposte/search`, {
        params: { cap: destinatario.cap, tipo: poste.consegnaTipo, indirizzo: destinatario.indirizzo || '' },
      });
      setPosteSearch({ loading: false, results: res.data.points || [], origin: res.data.origin || null });
    } catch (e) {
      setPosteSearch({ loading: false, results: null, origin: null });
      toast.error(e.response?.data?.detail || 'Ricerca punti non disponibile: inserisci il codice manualmente');
    }
  };

  const handleAuth = async () => {
    setAuthLoading(true);
    try {
      if (authMode === 'register') {
        await axios.post(`${API}/occasional/customer/register`, {
          email: authForm.email, password: authForm.password,
          first_name: authForm.first_name, last_name: authForm.last_name,
          tipo: authForm.tipo, company: authForm.company, vat: authForm.vat,
          codfis: authForm.codfis, sdi: authForm.sdi, pec: authForm.pec, phone: authForm.phone,
        });
        toast.success('Registrazione completata!');
      }
      const res = await axios.post(`${API}/occasional/customer/login`, {
        email: authForm.email, password: authForm.password,
      });
      const cust = { customer_id: res.data.customer_id, email: res.data.email, first_name: res.data.first_name };
      localStorage.setItem('occ_token', res.data.token || '');
      localStorage.setItem('occ_customer', JSON.stringify(cust));
      setAccount({ ...cust, mode: 'account' });
      // pre-fill mittente email/name
      setMittente((m) => ({ ...m, email: res.data.email || m.email }));
      toast.success(`Benvenuto${res.data.first_name ? ', ' + res.data.first_name : ''}!`);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Autenticazione non riuscita');
    } finally {
      setAuthLoading(false);
    }
  };

  const validateDest = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const res = await axios.post(`${API}/shipping/address/validate`, {
        addressLine1: destinatario.indirizzo, postalCode: destinatario.cap,
        city: destinatario.citta, state: destinatario.prov, countryCode: destinatario.nazione || 'IT',
      });
      setValidation(res.data);
    } catch (e) {
      toast.error('Verifica indirizzo non riuscita');
    } finally {
      setValidating(false);
    }
  };

  const partyValid = (p) => p.referente && p.indirizzo && p.cap && p.citta && p.prov && p.telefono && p.email;
  const colloValid = () => parseFloat(collo.peso) > 0;

  const goNext = async () => {
    if (step === 1 && !partyValid(mittente)) return toast.error('Compila tutti i campi del mittente');
    if (step === 2 && !partyValid(destinatario)) return toast.error('Compila tutti i campi del destinatario');
    if (step === 2 && locker.enabled && !(locker.code || '').trim())
      return toast.error('Seleziona o inserisci il codice del locker di destinazione');
    if (step === 2 && poste.consegnaEnabled && !(poste.code || '').trim())
      return toast.error('Seleziona o inserisci il codice del punto di consegna Poste');
    if (step === 3) {
      if (!colloValid()) return toast.error('Inserisci almeno il peso del pacco');
      setStep(4);        // advance immediately so the user sees the "Calcolo preventivo..." state
      fetchQuote();      // fire-and-forget; step 4 shows the loader then the price
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const fetchQuote = async () => {
    setQuoteLoading(true);
    setQuote(null);
    try {
      const res = await axios.post(`${API}/occasional/quote`, buildShipment());
      setQuote(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preventivo non disponibile');
    } finally {
      setQuoteLoading(false);
    }
  };

  const pay = async () => {
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/occasional/checkout/session`, {
        origin_url: window.location.origin,
        shipment: buildShipment(),
        customer_id: account.customer_id,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile avviare il pagamento');
      setPayLoading(false);
    }
  };

  // ---------- Confirmation screen ----------
  if (confirm) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center" data-testid="confirmation-card">
            {confirm.state === 'checking' && (
              <>
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-800">Verifica pagamento in corso...</h2>
                <p className="text-slate-500 mt-2 text-sm">Attendi qualche istante.</p>
              </>
            )}
            {confirm.state === 'paid' && (
              <>
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-slate-800">Pagamento riuscito!</h2>
                <p className="text-slate-600 mt-2">La tua spedizione è stata registrata.</p>
                {confirm.order && (
                  <p className="mt-3 text-sm text-slate-500">Ordine n. <span className="font-semibold text-slate-800">{confirm.order}</span></p>
                )}
                {confirm.tracking && (
                  <p className="mt-1 text-sm text-slate-500" data-testid="confirm-tracking">Tracking: <span className="font-semibold text-slate-800">{confirm.tracking}</span></p>
                )}
                {confirm.label && (
                  <a href={confirm.label} target="_blank" rel="noreferrer" data-testid="confirm-label-link"
                    className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                    <Package className="w-4 h-4" /> Scarica etichetta
                  </a>
                )}
                <Button className="mt-6 w-full" onClick={() => navigate('/spedizione')} data-testid="new-shipment-btn">
                  Nuova spedizione
                </Button>
                {localStorage.getItem('occ_token') && (
                  <Button variant="outline" className="mt-2 w-full" onClick={() => navigate('/le-mie-spedizioni')} data-testid="goto-my-shipments-btn">
                    Vai alle mie spedizioni
                  </Button>
                )}
              </>
            )}
            {(confirm.state === 'expired' || confirm.state === 'timeout') && (
              <>
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-slate-800">Pagamento non completato</h2>
                <p className="text-slate-500 mt-2 text-sm">La sessione è scaduta o è stata annullata.</p>
                <Button className="mt-6 w-full" onClick={() => navigate('/spedizione')} data-testid="retry-btn">
                  Riprova
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {payLoading && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex flex-col items-center justify-center" data-testid="pay-overlay">
          <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
          <p className="text-white font-medium">Ti stiamo reindirizzando al pagamento sicuro...</p>
          <p className="text-slate-300 text-sm mt-1">Attendi qualche istante, non chiudere la pagina.</p>
        </div>
      )}
      {/* Header */}
      <header className="bg-[#0a0a1a] text-white">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center gap-3">
          <Truck className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-lg font-semibold">Spedizione occasionale</h1>
            <p className="text-xs text-slate-400">Spedisci il tuo pacco in pochi passaggi</p>
          </div>
          <button onClick={() => navigate('/')} className="ml-auto text-sm text-slate-300 hover:text-white transition-colors" data-testid="home-link">
            ← Home
          </button>
          {account.mode === 'account' && (
            <button onClick={() => navigate('/le-mie-spedizioni')} className="ml-4 text-sm text-blue-300 hover:text-white transition-colors" data-testid="my-shipments-link">
              Le mie spedizioni
            </button>
          )}
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={s.key}>
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    active ? 'bg-blue-600 text-white' : done ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                    {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-medium ${active ? 'text-blue-600' : 'text-slate-400'}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-green-500' : 'bg-slate-200'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        <Card>
          <CardContent className="p-6 sm:p-8">
            {/* Step 0: Account */}
            {step === 0 && (
              <div className="space-y-6" data-testid="step-account">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">Accedi o continua come ospite</h2>
                  <p className="text-slate-500 text-sm mt-1">Accedi per ritrovare le tue spedizioni, oppure prosegui senza account.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setAuthMode('login')} data-testid="tab-login"
                    className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${authMode === 'login' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    <LogIn className="w-4 h-4" /> Accedi
                  </button>
                  <button onClick={() => setAuthMode('register')} data-testid="tab-register"
                    className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${authMode === 'register' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    <UserPlus className="w-4 h-4" /> Registrati
                  </button>
                </div>
                <div className="space-y-4">
                  {authMode === 'register' && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setAuthForm({ ...authForm, tipo: 'privato' })}
                          data-testid="tipo-privato"
                          className={`py-2 rounded-lg text-sm font-medium border transition-colors ${authForm.tipo === 'privato' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}>
                          Privato
                        </button>
                        <button type="button" onClick={() => setAuthForm({ ...authForm, tipo: 'azienda' })}
                          data-testid="tipo-azienda"
                          className={`py-2 rounded-lg text-sm font-medium border transition-colors ${authForm.tipo === 'azienda' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}>
                          Azienda
                        </button>
                      </div>
                      {authForm.tipo === 'azienda' ? (
                        <>
                          <div>
                            <Label>Ragione sociale</Label>
                            <Input data-testid="auth-company" value={authForm.company}
                              onChange={(e) => setAuthForm({ ...authForm, company: e.target.value })} />
                          </div>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                              <Label>P.IVA *</Label>
                              <Input data-testid="auth-vat" value={authForm.vat}
                                onChange={(e) => setAuthForm({ ...authForm, vat: e.target.value })} />
                            </div>
                            <div>
                              <Label>Codice SDI</Label>
                              <Input data-testid="auth-sdi" value={authForm.sdi}
                                onChange={(e) => setAuthForm({ ...authForm, sdi: e.target.value })} />
                            </div>
                          </div>
                          <div>
                            <Label>PEC</Label>
                            <Input data-testid="auth-pec" type="email" value={authForm.pec}
                              onChange={(e) => setAuthForm({ ...authForm, pec: e.target.value })} />
                          </div>
                        </>
                      ) : (
                        <div>
                          <Label>Codice Fiscale</Label>
                          <Input data-testid="auth-codfis" value={authForm.codfis}
                            onChange={(e) => setAuthForm({ ...authForm, codfis: e.target.value })} />
                        </div>
                      )}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>{authForm.tipo === 'azienda' ? 'Referente' : 'Nome'}</Label>
                          <Input data-testid="auth-firstname" value={authForm.first_name}
                            onChange={(e) => setAuthForm({ ...authForm, first_name: e.target.value })} />
                        </div>
                        <div>
                          <Label>{authForm.tipo === 'azienda' ? 'Cognome referente' : 'Cognome'}</Label>
                          <Input data-testid="auth-lastname" value={authForm.last_name}
                            onChange={(e) => setAuthForm({ ...authForm, last_name: e.target.value })} />
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="auth-email" type="email" value={authForm.email}
                      onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input data-testid="auth-password" type="password" value={authForm.password}
                      onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} />
                  </div>
                  <Button className="w-full" onClick={handleAuth} disabled={authLoading || !authForm.email || !authForm.password} data-testid="auth-submit">
                    {authLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {authMode === 'login' ? 'Accedi e continua' : 'Registrati e continua'}
                  </Button>
                </div>
                <div className="relative text-center">
                  <span className="bg-white px-3 text-xs text-slate-400 relative z-10">oppure</span>
                  <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-200" />
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="guest-invoice-notice">
                  Come ospite riceverai uno <strong>scontrino</strong>. Per ricevere la <strong>fattura</strong> registrati come Azienda con P.IVA.
                </div>
                <Button variant="outline" className="w-full" onClick={() => { setAccount({ ...account, mode: 'guest' }); setStep(1); }} data-testid="guest-btn">
                  Continua come ospite <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 1: Mittente */}
            {step === 1 && (
              <div data-testid="step-mittente">
                <h2 className="text-xl font-semibold text-slate-800 mb-1">Chi spedisce?</h2>
                <p className="text-slate-500 text-sm mb-6">Dati del mittente.</p>
                <PartyForm party={mittente} setParty={setMittente} prefix="mittente" />
              </div>
            )}

            {/* Step 2: Destinatario */}
            {step === 2 && (
              <div data-testid="step-destinatario">
                <h2 className="text-xl font-semibold text-slate-800 mb-1">A chi lo spedisci?</h2>
                <p className="text-slate-500 text-sm mb-6">Dati del destinatario. Verifica l'indirizzo per ridurre i mancati recapiti.</p>
                <PartyForm party={destinatario} setParty={(p) => { setDestinatario(p); setValidation(null); }}
                  prefix="destinatario" onValidate={validateDest} validation={validation} validating={validating} />

                {/* Consegna a locker InPost (Locker-to-Locker) */}
                <div className="mt-6 rounded-xl border border-slate-200 p-4">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="locker-toggle">
                    <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={locker.enabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setLocker({ enabled, code: '', name: '', address: '' });
                        setLockerSearch({ loading: false, results: null });
                        if (enabled) {
                          setRitiro({ richiesto: false, data: '', note: '' });
                          setPoste((p) => ({ ...p, consegnaEnabled: false, code: '', name: '', address: '' }));
                          setPosteSearch({ loading: false, results: null });
                        }
                      }} />
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Boxes className="w-4 h-4 text-blue-500" /> Consegna a un locker InPost (Locker-to-Locker)
                    </span>
                  </label>

                  {locker.enabled && (
                    <div className="mt-4 space-y-4">
                      <p className="text-xs text-slate-500">
                        Il pacco verrà consegnato al locker InPost scelto. Cerca i locker vicini al CAP del destinatario
                        oppure inserisci direttamente il codice punto (es. ITLLT02445P).
                      </p>

                      {locker.code ? (
                        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm flex items-start gap-2" data-testid="locker-selected">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <div>
                            <div className="font-medium text-green-800">Locker selezionato: {locker.code}</div>
                            {(locker.name || locker.address) && (
                              <div className="text-green-700 text-xs mt-0.5">{[locker.name, locker.address].filter(Boolean).join(' · ')}</div>
                            )}
                            <button type="button" className="text-xs text-green-700 underline mt-1"
                              onClick={() => setLocker({ ...locker, code: '', name: '', address: '' })} data-testid="locker-clear">
                              Cambia locker
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label>Codice locker di destinazione</Label>
                              <Input data-testid="locker-code" value={locker.code}
                                onChange={(e) => setLocker({ ...locker, code: e.target.value.toUpperCase() })}
                                placeholder="ITLLT02445P" />
                            </div>
                            <Button type="button" variant="outline" onClick={searchLockers} disabled={lockerSearch.loading}
                              data-testid="locker-search-btn">
                              {lockerSearch.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                              Cerca vicino al CAP
                            </Button>
                          </div>

                          {lockerSearch.results && (
                            lockerSearch.results.length > 0 ? (
                              <div className="space-y-2">
                                <PointsMap
                                  points={lockerSearch.results}
                                  origin={lockerSearch.origin}
                                  selectedCode={locker.code}
                                  onSelect={(p) => { setLocker({ enabled: true, code: p.code, name: p.name, address: p.address }); setLockerSearch({ loading: false, results: null, origin: null }); }}
                                />
                                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="locker-results">
                                  {lockerSearch.results.map((p, i) => (
                                    <button type="button" key={p.code || i}
                                      className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                                      onClick={() => { setLocker({ enabled: true, code: p.code, name: p.name, address: p.address }); setLockerSearch({ loading: false, results: null, origin: null }); }}
                                      data-testid={`locker-result-${i}`}>
                                      <div className="text-sm font-medium text-slate-800">{p.code}{p.name ? ` · ${p.name}` : ''}</div>
                                      {(p.address || isFiniteNum(p.distance)) && (
                                        <div className="text-xs text-slate-500">{[p.address, fmtDistance(p.distance)].filter(Boolean).join(' · ')}</div>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-slate-500">Nessun locker trovato per questo CAP. Inserisci il codice manualmente.</div>
                            )
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* PDB Poste: consegna presso Punto Poste / Ufficio Postale / Locker Poste */}
                <div className="mt-4 rounded-xl border border-slate-200 p-4">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="poste-toggle">
                    <input type="checkbox" className="w-4 h-4 accent-red-600" checked={poste.consegnaEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setPoste((p) => ({ ...p, consegnaEnabled: enabled, code: '', name: '', address: '' }));
                        setPosteSearch({ loading: false, results: null });
                        if (enabled) {
                          setLocker({ enabled: false, code: '', name: '', address: '' });
                          setLockerSearch({ loading: false, results: null });
                        }
                      }} />
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Landmark className="w-4 h-4 text-red-500" /> Consegna a un punto Poste (Punto Poste / Ufficio Postale / Locker)
                    </span>
                  </label>

                  {poste.consegnaEnabled && (
                    <div className="mt-4 space-y-4">
                      <div className="flex flex-wrap gap-2" data-testid="poste-tipo">
                        {[
                          { v: 'PUNTO_POSTE', l: 'Punto Poste' },
                          { v: 'UFFICIO_POSTALE', l: 'Ufficio Postale' },
                          { v: 'LOCKER', l: 'Locker Poste' },
                        ].map((t) => (
                          <button type="button" key={t.v}
                            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${poste.consegnaTipo === t.v ? 'bg-red-50 border-red-300 text-red-700 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                            onClick={() => { setPoste((p) => ({ ...p, consegnaTipo: t.v, code: '', name: '', address: '' })); setPosteSearch({ loading: false, results: null }); }}
                            data-testid={`poste-tipo-${t.v}`}>
                            {t.l}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500">Servizio:</span>
                        {[{ v: 'PDBST', l: 'Standard' }, { v: 'PDBEX', l: 'Express' }].map((sv) => (
                          <button type="button" key={sv.v}
                            className={`px-3 py-1.5 rounded-lg border transition-colors ${poste.servizio === sv.v ? 'bg-slate-800 border-slate-800 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                            onClick={() => setPoste((p) => ({ ...p, servizio: sv.v }))}
                            data-testid={`poste-servizio-${sv.v}`}>
                            {sv.l}
                          </button>
                        ))}
                      </div>

                      <p className="text-xs text-slate-500">
                        Il pacco verrà consegnato al punto scelto (il destinatario lo ritira lì). Cerca i punti vicini
                        al CAP del destinatario oppure inserisci direttamente il codice punto.
                      </p>

                      {poste.code ? (
                        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm flex items-start gap-2" data-testid="poste-selected">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <div>
                            <div className="font-medium text-green-800">Punto selezionato: {poste.code}</div>
                            {(poste.name || poste.address) && (
                              <div className="text-green-700 text-xs mt-0.5">{[poste.name, poste.address].filter(Boolean).join(' · ')}</div>
                            )}
                            <button type="button" className="text-xs text-green-700 underline mt-1"
                              onClick={() => setPoste((p) => ({ ...p, code: '', name: '', address: '' }))} data-testid="poste-clear">
                              Cambia punto
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label>Codice punto di consegna</Label>
                              <Input data-testid="poste-code" value={poste.code} maxLength={10}
                                onChange={(e) => setPoste((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                                placeholder="es. 12345" />
                            </div>
                            <Button type="button" variant="outline" onClick={searchPoste} disabled={posteSearch.loading}
                              data-testid="poste-search-btn">
                              {posteSearch.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                              Cerca vicino al CAP
                            </Button>
                          </div>

                          {posteSearch.results && (
                            posteSearch.results.length > 0 ? (
                              <div className="space-y-2">
                                <PointsMap
                                  points={posteSearch.results}
                                  origin={posteSearch.origin}
                                  selectedCode={poste.code}
                                  onSelect={(p) => { setPoste((prev) => ({ ...prev, code: p.code, name: p.name, address: p.address })); setPosteSearch({ loading: false, results: null, origin: null }); }}
                                />
                                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="poste-results">
                                  {posteSearch.results.map((p, i) => (
                                    <button type="button" key={p.code || i}
                                      className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                                      onClick={() => { setPoste((prev) => ({ ...prev, code: p.code, name: p.name, address: p.address })); setPosteSearch({ loading: false, results: null, origin: null }); }}
                                      data-testid={`poste-result-${i}`}>
                                      <div className="text-sm font-medium text-slate-800">{p.code}{p.name ? ` · ${p.name}` : ''}</div>
                                      {(p.address || p.hours || isFiniteNum(p.distance)) && (
                                        <div className="text-xs text-slate-500">{[p.address, p.hours, fmtDistance(p.distance)].filter(Boolean).join(' · ')}</div>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-slate-500">Nessun punto trovato per questo CAP. Inserisci il codice manualmente.</div>
                            )
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Pacco */}
            {step === 3 && (
              <div data-testid="step-pacco">
                <h2 className="text-xl font-semibold text-slate-800 mb-1">Il tuo pacco</h2>
                <p className="text-slate-500 text-sm mb-6">Peso e dimensioni del collo.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1.5"><Weight className="w-4 h-4" /> Peso (kg) *</Label>
                    <Input data-testid="collo-peso" type="number" step="0.1" value={collo.peso}
                      onChange={(e) => setCollo({ ...collo, peso: e.target.value })} placeholder="2.5" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> Lunghezza (cm)</Label>
                    <Input data-testid="collo-lunghezza" type="number" value={collo.lunghezza}
                      onChange={(e) => setCollo({ ...collo, lunghezza: e.target.value })} placeholder="30" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> Larghezza (cm)</Label>
                    <Input data-testid="collo-larghezza" type="number" value={collo.larghezza}
                      onChange={(e) => setCollo({ ...collo, larghezza: e.target.value })} placeholder="20" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> Altezza (cm)</Label>
                    <Input data-testid="collo-altezza" type="number" value={collo.altezza}
                      onChange={(e) => setCollo({ ...collo, altezza: e.target.value })} placeholder="15" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label>Contrassegno € (facoltativo)</Label>
                    <Input data-testid="opt-contrassegno" type="number" step="0.01" value={opts.contrassegno}
                      onChange={(e) => setOpts({ ...opts, contrassegno: e.target.value })} placeholder="0" />
                  </div>
                  <div>
                    <Label>Valore assicurato € (facoltativo)</Label>
                    <Input data-testid="opt-assicurazione" type="number" step="0.01" value={opts.valore_assicurato}
                      onChange={(e) => setOpts({ ...opts, valore_assicurato: e.target.value })} placeholder="0" />
                  </div>
                </div>
                <div className="mt-4">
                  <Label>Note per il corriere (facoltativo)</Label>
                  <Textarea data-testid="opt-note" value={opts.note}
                    onChange={(e) => setOpts({ ...opts, note: e.target.value })} placeholder="Es. Consegna al piano terra" />
                </div>

                {locker.enabled ? (
                  <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 flex items-start gap-2" data-testid="locker-dropoff-note">
                    <Boxes className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                    <span>Consegna a locker InPost{locker.code ? ` (${locker.code})` : ''}: deposita il pacco in un locker InPost. Nessun ritiro a domicilio.</span>
                  </div>
                ) : (
                <>
                {/* PDB Poste: drop-off / accettazione presso un punto — alternativa al ritiro a domicilio */}
                <div className="mt-6 rounded-xl border border-slate-200 p-4">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="poste-dropoff-toggle">
                    <input type="checkbox" className="w-4 h-4 accent-red-600" checked={poste.dropoffEnabled}
                      onChange={(e) => setPoste((p) => ({ ...p, dropoffEnabled: e.target.checked }))} />
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <PackageCheck className="w-4 h-4 text-red-500" /> Deposita il pacco presso un punto Poste (drop-off, invece del ritiro a domicilio)
                    </span>
                  </label>
                  {poste.dropoffEnabled && (
                    <div className="mt-4 space-y-3">
                      <div className="flex flex-wrap gap-2" data-testid="poste-dropoff-tipo">
                        {[
                          { v: 'PUNTO_POSTE', l: 'Punto Poste' },
                          { v: 'UFFICIO_POSTALE', l: 'Ufficio Postale' },
                          { v: 'LOCKER', l: 'Locker Poste' },
                        ].map((t) => (
                          <button type="button" key={t.v}
                            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${poste.dropoffTipo === t.v ? 'bg-red-50 border-red-300 text-red-700 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                            onClick={() => setPoste((p) => ({ ...p, dropoffTipo: t.v }))}
                            data-testid={`poste-dropoff-tipo-${t.v}`}>
                            {t.l}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500">
                        Porterai tu il pacco al punto Poste scelto: nessun ritiro a domicilio.
                        {!posteConsegnaActive && ' La spedizione userà il servizio PosteDelivery Business.'}
                      </p>
                    </div>
                  )}
                </div>

                {!poste.dropoffEnabled && (
                <div className="mt-4 rounded-xl border border-slate-200 p-4">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="ritiro-toggle">
                    <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={ritiro.richiesto}
                      onChange={(e) => setRitiro({ ...ritiro, richiesto: e.target.checked })} />
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Truck className="w-4 h-4 text-blue-500" /> Prenota ritiro a domicilio
                    </span>
                  </label>
                  {ritiro.richiesto && (
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label>Data ritiro *</Label>
                        <Input data-testid="ritiro-data" type="date" value={ritiro.data}
                          min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                          onChange={(e) => setRitiro({ ...ritiro, data: e.target.value })} />
                      </div>
                      <div>
                        <Label>Note ritiro (facoltativo)</Label>
                        <Input data-testid="ritiro-note" value={ritiro.note}
                          onChange={(e) => setRitiro({ ...ritiro, note: e.target.value })} placeholder="Es. citofono Rossi" />
                      </div>
                    </div>
                  )}
                </div>
                )}
                </>
                )}
              </div>
            )}

            {/* Step 4: Pagamento */}
            {step === 4 && (
              <div data-testid="step-pagamento">
                <h2 className="text-xl font-semibold text-slate-800 mb-1">Riepilogo e pagamento</h2>
                <p className="text-slate-500 text-sm mb-6">Controlla i dati e procedi al pagamento sicuro.</p>

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Home className="w-4 h-4" /> Mittente</div>
                    <div className="text-slate-600">{mittente.referente}</div>
                    <div className="text-slate-500">{mittente.indirizzo}, {mittente.cap} {mittente.citta} ({mittente.prov})</div>
                    {posteDropoffActive && (
                      <div className="mt-2 pt-2 border-t border-slate-200 text-slate-600 flex items-start gap-1.5" data-testid="summary-poste-dropoff">
                        <PackageCheck className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                        <span>Drop-off presso <span className="font-medium">{posteTipoLabel(poste.dropoffTipo)}</span>: nessun ritiro a domicilio</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Destinatario</div>
                    <div className="text-slate-600">{destinatario.referente}</div>
                    <div className="text-slate-500">{destinatario.indirizzo}, {destinatario.cap} {destinatario.citta} ({destinatario.prov})</div>
                    {lockerActive && (
                      <div className="mt-2 pt-2 border-t border-slate-200 text-slate-600 flex items-start gap-1.5" data-testid="summary-locker">
                        <Boxes className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                        <span>Consegna al locker InPost <span className="font-medium">{locker.code}</span>{locker.address ? ` · ${locker.address}` : ''}</span>
                      </div>
                    )}
                    {posteConsegnaActive && (
                      <div className="mt-2 pt-2 border-t border-slate-200 text-slate-600 flex items-start gap-1.5" data-testid="summary-poste-consegna">
                        <Landmark className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                        <span>Consegna presso <span className="font-medium">{posteTipoLabel(poste.consegnaTipo)}</span> <span className="font-medium">{poste.code}</span>{poste.address ? ` · ${poste.address}` : ''} ({poste.servizio === 'PDBEX' ? 'Express' : 'Standard'})</span>
                      </div>
                    )}
                  </div>
                </div>

                {quoteLoading && (
                  <div className="flex items-center justify-center py-8 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Calcolo preventivo...
                  </div>
                )}

                {quote && !quoteLoading && (
                  <div className="border border-blue-100 bg-blue-50 rounded-xl p-5 mb-6" data-testid="quote-box">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-slate-500">Totale spedizione</div>
                        <div className="text-3xl font-bold text-slate-800" data-testid="quote-total">
                          € {Number(quote.prezzo_totale).toFixed(2)}
                        </div>
                      </div>
                      {quote.logo_url && <img src={quote.logo_url} alt="corriere" className="h-10 object-contain" />}
                    </div>
                    <div className="text-xs text-slate-500 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                      {quote.prezzo_spedizione != null && <span>Spedizione: € {Number(quote.prezzo_spedizione).toFixed(2)}</span>}
                      {quote.fuel_surcharge != null && <span>Fuel: € {Number(quote.fuel_surcharge).toFixed(2)}</span>}
                    </div>
                  </div>
                )}

                <Button className="w-full h-12 text-base" onClick={pay} disabled={!quote || payLoading} data-testid="pay-btn">
                  {payLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                  Paga € {quote ? Number(quote.prezzo_totale).toFixed(2) : '—'} in sicurezza
                </Button>
                <p className="text-center text-xs text-slate-400 mt-3 flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Pagamento protetto con Stripe
                </p>
              </div>
            )}

            {/* Navigation */}
            {step > 0 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
                </Button>
                {step < 4 && (
                  <Button onClick={goNext} data-testid="next-btn">
                    {step === 3 ? 'Vai al pagamento' : 'Continua'} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OccasionalShipping;
